/**
 * Pivot Header Component - Trailer Word Rotation
 */

import { getAllWords } from './rotatingText.js';

export interface HeaderState {
  element: HTMLElement | null;
  logoImage: HTMLImageElement | null;
  logoLoaded: boolean;
  hasPlayed: boolean;
}

let headerState: HeaderState = {
  element: null,
  logoImage: null,
  logoLoaded: false,
  hasPlayed: false
};

export function createHeader(): HTMLElement {
  if (headerState.element) return headerState.element;

  const words = getAllWords();
  const finalWord = words[words.length - 1];

  const header = document.createElement('div');
  header.className = 'pivot-header';

  header.innerHTML = `
    <div class="pivot-header-content">
      <img class="pivot-logo" src="/logo.png" alt="Pivot Logo" />
      <span class="pivot-title">Pivot</span>
      <span class="pivot-dot">•</span>
      <span class="pivot-tagline">
        <span class="pivot-tagline-prefix">The</span>
        <span class="pivot-tagline-word-container">
          <span class="pivot-tagline-word" id="pivot-tagline-word"></span>
        </span>
        <span class="pivot-tagline-suffix">Engine</span>
      </span>
    </div>
  `;

  // Logo handling
  const logoImg = header.querySelector('.pivot-logo') as HTMLImageElement;
  if (logoImg) {
    logoImg.onload = () => (logoImg.style.opacity = '1');
    logoImg.onerror = () => (logoImg.style.display = 'none');
    headerState.logoImage = logoImg;
  }

  // Rotating text animation
  const wordElement = header.querySelector('#pivot-tagline-word') as HTMLElement;
  if (wordElement && !headerState.hasPlayed) {
    const container = wordElement.parentElement as HTMLElement | null;
    playWordTrailer(wordElement, container, words);
    headerState.hasPlayed = true;
  }

  headerState.element = header;
  return header;
}

function playWordTrailer(target: HTMLElement, container: HTMLElement | null, words: string[]) {
  let i = 0;
  const interval = 110; // speed (ms per word)
  const stopOn = words.length - 1;
  const finalWord = words[stopOn];

  const run = setInterval(() => {
    target.textContent = words[i];
    i++;

    if (i > stopOn) {
      clearInterval(run);

      // Hold final word
      target.textContent = finalWord;
      target.classList.add('pivot-tagline-word-final');

      // Measure final width to shrink-wrap container
      if (container) {
        const measurer = document.createElement('span');
        measurer.className = 'pivot-tagline-word pivot-tagline-word-measurer';
        measurer.textContent = finalWord;
        document.body.appendChild(measurer);
        const finalWidth = measurer.getBoundingClientRect().width;
        measurer.remove();

        // Add small breathing room
        const bufferWidth = Math.ceil(finalWidth * 1.35);
        container.style.width = `${bufferWidth}px`;
      }
    }
  }, interval);
}

export function getHeaderState(): Readonly<HeaderState> {
  return { ...headerState };
}
